/**
 * Client-side document head helpers for SPA routes.
 * Social crawlers (WhatsApp, Facebook, etc.) read the static public/index.html
 * response — these helpers improve the browser tab after JS loads.
 */

const DEFAULT_DESCRIPTION =
  'Explore unforgettable tours, holiday packages, sightseeing experiences, and luxury travel with NGK Tours.';

const PRODUCTION_ORIGIN = 'https://ngktours.com';
const PRODUCTION_PORTAL = `${PRODUCTION_ORIGIN}/portal`;
const SOCIAL_IMAGE = `${PRODUCTION_ORIGIN}/images/ngk-social-preview.jpg`;

function upsertMeta(
  attr: 'name' | 'property',
  key: string,
  content: string
): void {
  if (typeof document === 'undefined') return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string): void {
  if (typeof document === 'undefined') return;
  let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export interface PageMetaInput {
  title: string;
  description?: string;
  /** Absolute canonical URL for this page (production). */
  canonical?: string;
  /** Absolute page URL for og:url (defaults to portal). */
  url?: string;
  image?: string;
  imageAlt?: string;
}

/**
 * Apply page metadata for the current SPA route (browser only).
 * Keep titles unique per route; fall back to NGK Tours defaults.
 */
export function setPageMeta({
  title,
  description = DEFAULT_DESCRIPTION,
  canonical = `${PRODUCTION_ORIGIN}/`,
  url = PRODUCTION_PORTAL,
  image = SOCIAL_IMAGE,
  imageAlt = 'NGK Tours travel experiences across the UAE and Egypt',
}: PageMetaInput): void {
  if (typeof document === 'undefined') return;

  document.title = title;

  upsertMeta('name', 'description', description);
  upsertLink('canonical', canonical);

  upsertMeta('property', 'og:title', title);
  upsertMeta('property', 'og:description', description);
  upsertMeta('property', 'og:type', 'website');
  upsertMeta('property', 'og:url', url);
  upsertMeta('property', 'og:site_name', 'NGK Tours');
  upsertMeta('property', 'og:image', image);
  upsertMeta('property', 'og:image:alt', imageAlt);
  upsertMeta('property', 'og:image:width', '1200');
  upsertMeta('property', 'og:image:height', '630');
  upsertMeta('property', 'og:locale', 'en_US');

  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:title', title);
  upsertMeta('name', 'twitter:description', description);
  upsertMeta('name', 'twitter:image', image);
}

export const NGK_SITE = {
  origin: PRODUCTION_ORIGIN,
  portal: PRODUCTION_PORTAL,
  socialImage: SOCIAL_IMAGE,
  defaultDescription: DEFAULT_DESCRIPTION,
  defaultTitle: 'NGK Tours | Discover the UAE & Egypt',
};
